import React, { useState, useRef, useMemo } from 'react';
import { Send, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../components/config'

const BOOKING_DAYS = [1, 4]; // Monday and Thursday

function getNextBookingDates(bookingDays) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return bookingDays.map(bookingDay => {
    const todayDay = today.getDay();
    let daysUntilNext = (bookingDay - todayDay + 7) % 7;
    if (daysUntilNext === 0) daysUntilNext = 7;
    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + daysUntilNext);
    return nextDate;
  });
}

function getRandomTimes(startHour = 11, endHour = 16, count = 3) {
  const times = [];
  while (times.length < count) {
    const hour = Math.floor(Math.random() * (endHour - startHour)) + startHour;
    const minute = Math.random() < 0.5 ? 0 : 30;
    const timeStr = `${hour.toString().padStart(2, '0')}:${minute === 0 ? '00' : '30'}`;
    if (!times.includes(timeStr)) {
      times.push(timeStr);
    }
  }
  times.sort();
  return times;
}

const ScheduleConsultation = () => {
  const navigate = useNavigate();
  const formRef = useRef();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    date: '',
    time: '',
    message: ''
  });
  const [status, setStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const nextBookingDates = useMemo(() => getNextBookingDates(BOOKING_DAYS), []);
  const randomTimes = useMemo(() => getRandomTimes(11, 17, 3), []); // 11:00 to 16:30

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validate = () => {
    if (!formData.name.trim()) {
      setStatus("Full name is required.");
      return false;
    }
    if (!formData.email.trim()) {
      setStatus("Email address is required.");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setStatus("Please enter a valid email address.");
      return false;
    }
    if (!formData.date.trim()) {
      setStatus("Please select a preferred date.");
      return false;
    }
    if (!formData.time.trim()) {
      setStatus("Please select a preferred time.");
      return false;
    }
    if (!formData.message.trim()) {
      setStatus("Please enter a message.");
      return false;
    }
    return true;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('');
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/consultations`, formData);
      setStatus('Your consultation request has been sent!');
      setFormData({
        name: '',
        email: '',
        company: '',
        date: '',
        time: '',
        message: ''
      });
      navigate('/thank-you?from=consultation');
    } catch (err) {
      setStatus('Failed to send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white font-light">
      {/* Hero Section */}
      <div className="relative py-20 px-6">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black opacity-90"></div>
        <div className="absolute inset-0 opacity-5">
          <div className="w-full h-full" style={{
            backgroundImage: `radial-gradient(circle at 30px 30px, white 2px, transparent 2px)`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center pt-8">
          <p className="text-xs tracking-[0.3em] md:text-[10px] uppercase text-gray-400 mb-8">
            Consultation • Expert Guidance • Strategic Planning
          </p>
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-white to-transparent mx-auto mb-8"></div>
          <p className="text-xl md:text-2xl font-light leading-relaxed max-w-3xl mx-auto tracking-wide">
            Schedule a strategic consultation to discuss your goals and explore how we can help transform your vision into reality.
          </p>
        </div>
      </div>

      {/* Form Section */}
      <div className="bg-white text-black">
        <div className="max-w-4xl mx-auto px-6 py-20">
          <div className="mb-10">
            <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-4">Consultation Form</p>
            <h2 className="text-4xl font-thin mb-6">
              Schedule Your
              <br />
              <em className="text-gray-400">Consultation</em>
            </h2>
            <div className="w-20 h-px bg-gray-300 mb-8"></div>
          </div>

          {/* Status Messages */}
          {submitting && (
            <div className="text-center text-gray-600 bg-gray-100 rounded-none py-4 mb-8 border-l-4 border-gray-400">
              <p className="font-light tracking-wide">Processing your consultation request...</p>
            </div>
          )}
          {status && !submitting && (
            <div className={`text-center py-4 mb-8 border-l-4 ${
              status.includes('sent') 
                ? 'text-green-700 bg-green-50 border-green-400' 
                : 'text-red-700 bg-red-50 border-red-400'
            }`}>
              <p className="font-light">{status}</p>
            </div>
          )}

          <form ref={formRef} onSubmit={handleSubmit} className="space-y-12">
            {/* Personal Information Section */}
            <div className="pb-8">
              <h3 className="text-2xl font-thin mb-8 tracking-wide">Personal Details</h3>
              
              <div className="space-y-8">
                <div className="group">
                  <label htmlFor="name" className="block text-gray-700 text-sm font-light mb-3 tracking-wide uppercase text-xs">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your full name"
                    className="w-full bg-transparent border-b border-gray-300 py-3 text-black placeholder-gray-400 focus:outline-none focus:border-black transition-all duration-300 font-light"
                  />
                </div>

                <div className="group">
                  <label htmlFor="email" className="block text-gray-700 text-sm font-light mb-3 tracking-wide uppercase text-xs">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="your.email@company.com"
                    className="w-full bg-transparent border-b border-gray-300 py-3 text-black placeholder-gray-400 focus:outline-none focus:border-black transition-all duration-300 font-light"
                  />
                </div>

                <div className="group">
                  <label htmlFor="company" className="block text-gray-700 text-sm font-light mb-3 tracking-wide uppercase text-xs">
                    Company / Organization
                  </label>
                  <input
                    id="company"
                    name="company"
                    type="text"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="Your company name"
                    className="w-full bg-transparent border-b border-gray-300 py-3 text-black placeholder-gray-400 focus:outline-none focus:border-black transition-all duration-300 font-light"
                  />
                </div>
              </div>
            </div>

            {/* Schedule Section */}
            <div className="pb-8">
              <h3 className="text-2xl font-thin mb-8 tracking-wide">Schedule Preferences</h3>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="group">
                  <label htmlFor="date" className="block text-gray-700 text-sm font-light mb-3 tracking-wide uppercase text-xs">
                    Preferred Date <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-transparent border-b border-gray-300 py-3 text-black focus:outline-none focus:border-black transition-all duration-300 font-light"
                  >
                    <option value="">Select a date</option>
                    {nextBookingDates.map(date => {
                      const iso = date.toISOString().split("T")[0];
                      return (
                        <option key={iso} value={iso}>
                          {date.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="group">
                  <label htmlFor="time" className="block text-gray-700 text-sm font-light mb-3 tracking-wide uppercase text-xs">
                    Preferred Time <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="time"
                    name="time"
                    value={formData.time}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-transparent border-b border-gray-300 py-3 text-black focus:outline-none focus:border-black transition-all duration-300 font-light"
                  >
                    <option value="">Select a time</option>
                    {randomTimes.map(time => (
                      <option key={time} value={time}>
                        {`${parseInt(time.split(':')[0], 10) > 12 ? parseInt(time.split(':')[0], 10) - 12 : time.split(':')[0]}:${time.split(':')[1]} ${parseInt(time.split(':')[0], 10) >= 12 ? 'PM' : 'AM'}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Message Section */}
            <div className="space-y-8">
              <h3 className="text-2xl font-thin mb-8 tracking-wide">Consultation Details</h3>
              
              <div className="group">
                <label htmlFor="message" className="block text-gray-700 text-sm font-light mb-3 tracking-wide uppercase text-xs">
                  Message & Goals <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={5}
                  required
                  placeholder="Tell us about your goals, challenges, or questions you'd like to discuss during the consultation..."
                  className="w-full bg-transparent border border-gray-300 p-4 text-black placeholder-gray-400 focus:outline-none focus:border-black transition-all duration-300 font-light resize-none"
                />
              </div>
            </div>

            {/* Submit Section */}
            <div className="pt-12 text-center">
              <button
                type="submit"
                disabled={submitting}
                className="group bg-black text-white px-16 py-4 text-sm tracking-[0.2em] uppercase hover:bg-gray-800 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="flex items-center justify-center gap-3">
                  <Send size={18} />
                  {submitting ? "Scheduling Consultation..." : "Schedule Consultation"}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Footer Quote */}
      <div className="bg-gray-100 py-10 px-6 text-black">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-6xl font-thin text-gray-800 mb-6">"</div>
          <blockquote className="text-2xl md:text-3xl font-light leading-relaxed tracking-wide">
            Great achievements begin with strategic conversations and
            <em className="text-gray-400"> clear vision.</em>
          </blockquote>
        </div>
      </div>
    </div>
  );
};

export default ScheduleConsultation;